import { SecretNetworkClient } from "secretjs";
import _ from "lodash";

const currencyToJson = (c: string) => {
  try {
    const m = JSON.parse(c);
    return {
      method: m.method || "",
      currency: m.currency || "",
      contact: m.contact || "",
    };
  } catch (err) {
    return {
      method: "",
      contact: "",
      currency: c,
    };
  }
};

const methodToJson = (c: string) => {
  try {
    const m = JSON.parse(c);
    return {
      method: m.method,
      contact: m.contact,
    };
  } catch (err) {
    return {
      method: c,
      contact: "",
    };
  }
};

export interface P2PPaymentInfo {
  method: string;
  detail: string;
  contact: string;
}

export interface P2PConfig {
  name: string;
  decimals: number;
  contract_address: string;
  code_hash: string;
  // Query Result
  admins: string[];
  deal_commission: string;
  deal_token: {
    address: string;
    code_hash: string;
  };
  governance: null; // TODO
  query_auth: {
    address: string;
    code_hash: string;
  };
}

export interface P2PDeal {
  deal_id: string;
  post_id: string;
  is_dealer_buy: boolean;
  amount: number;
  deal_token: {
    address: string;
    code_hash: string;
  };
  settle_currency: {
    method: string;
    currency: string;
    contact: string;
  };
  settle_price: number;
  dealer_deposit: boolean;
  customer_deposit: boolean;
  dealer: string;
  customer: string;
  state: string;
  resolver: string;
  expiry: string;
}

export interface P2PPost {
  post_id: string;
  is_dealer_buy: boolean;
  amount: number;
  min_amount: number;
  deal_token: {
    address: string;
    code_hash: string;
  };
  settle_currency: {
    method: string;
    currency: string;
    contact: string;
  };
  settle_price: number;
  dealer_deposit: boolean;
  dealer: string;
  state: string;
  expiry: string;
  deals: P2PDeal[];
}

// Get P2P Details (commission, expiry, etc)
export const fetchP2PConfigs = async (
  secretjs: SecretNetworkClient
): Promise<P2PConfig[]> => {
  const result: any = await secretjs.query.compute.queryContract({
    contract_address: process.env.NEXT_PUBLIC_P2P_CONTRACT_ADDRESS || "",
    code_hash: process.env.NEXT_PUBLIC_P2P_CONTRACT_CODE_HASH || "",
    query: { config: {} },
  });
  const configs = [
    {
      ...result.config.config,
      contract_address: process.env.NEXT_PUBLIC_P2P_CONTRACT_ADDRESS || "",
      code_hash: process.env.NEXT_PUBLIC_P2P_CONTRACT_CODE_HASH || "",
      deal_token: result.config.config.deal_token_a,
      name: "sSCRT",
      decimals: 6,
    },
    {
      ...result.config.config,
      contract_address: process.env.NEXT_PUBLIC_P2P_CONTRACT_ADDRESS || "",
      code_hash: process.env.NEXT_PUBLIC_P2P_CONTRACT_CODE_HASH || "",
      deal_token: result.config.config.deal_token_b,
      name: "sUSDC (Noble)",
      decimals: 6,
    },
    {
      ...result.config.config,
      contract_address: process.env.NEXT_PUBLIC_P2P_CONTRACT_ADDRESS || "",
      code_hash: process.env.NEXT_PUBLIC_P2P_CONTRACT_CODE_HASH || "",
      deal_token: result.config.config.deal_token_c,
      name: "SILK",
      decimals: 6,
    },
  ];
  console.log({ result });
  return configs;
};

// Get Token Balance
export const fetchSnipTokenBalance = async (
  params: {
    contract_address: string;
    code_hash: string;
    chain_id: string;
    address: string;
    decimals: number;
  },
  secretjs: SecretNetworkClient,
  disableSuggest?: boolean
): Promise<number> => {
  try {
    const key = await window.keplr?.getSecret20ViewingKey(
      params.chain_id,
      params.contract_address
    );
    const result: any = await secretjs.query.compute.queryContract({
      contract_address: params.contract_address,
      code_hash: params.code_hash,
      query: { balance: { key, address: params.address } },
    });
    if (result.viewing_key_error) {
      throw new Error(result.viewing_key_error.msg);
    } else {
      return (
        Number(_.get(result, "balance.amount", "0")) / 10 ** params.decimals
      );
    }
  } catch (err: any) {
    if (!disableSuggest && err.message === "There is no matched secret20") {
      await window.keplr?.suggestToken(
        params.chain_id,
        params.contract_address
      );
      return await fetchSnipTokenBalance(params, secretjs, true);
    } else {
      throw new Error(err.message);
    }
  }
};

// Get Posts and Deals
export const fetchP2PPosts = async (
  params: { contract_address: string; code_hash: string },
  secretjs: SecretNetworkClient
) => {
  const postsResult: any = await secretjs.query.compute.queryContract({
    contract_address: params.contract_address,
    code_hash: params.code_hash,
    query: { active_posts: {} },
  });
  const dealsResult: any = await secretjs.query.compute.queryContract({
    contract_address: params.contract_address,
    code_hash: params.code_hash,
    query: { active_deals: {} },
  });

  const dealsMap = _.groupBy(dealsResult?.active_deals?.deals || [], "post_id");
  const posts = (postsResult?.active_posts?.posts || [])
    .map((p: any) => ({
      ...p,
      settle_currency: currencyToJson(p.settle_currency),
      deals: (dealsMap[p.post_id] || []).reverse().map((d) => ({
        ...d,
        settle_currency: currencyToJson(d.settle_currency),
      })),
    }))
    .reverse();
  console.log({ posts, dealsMap });
  return posts;
};

// Get My Payment Info
export const fetchMyPaymentInfo = async (
  params: {
    contract_address: string;
    code_hash: string;
    address: string;
    viewing_key: string;
  },
  secretjs: SecretNetworkClient
) => {
  console.log({
    contract_address: params.contract_address,
    code_hash: params.code_hash,
    query: {
      my_payment_info: {
        key: params.viewing_key,
        address: params.address,
      },
    },
  });
  const result: any = await secretjs.query.compute.queryContract({
    contract_address: params.contract_address,
    code_hash: params.code_hash,
    query: {
      my_payment_info: {
        key: params.viewing_key,
        address: params.address,
      },
    },
  });
  if (typeof result === "string") {
    throw new Error(result);
  }

  const paymentInfo = { ...result, ...methodToJson(result.method) };
  console.log({ paymentInfo });
  return paymentInfo;
};

// Get Deal Payment Info
export const fetchDealPaymentInfo = async (
  params: {
    contract_address: string;
    code_hash: string;
    address: string;
    viewing_key: string;
    deal_id: string;
  },
  secretjs: SecretNetworkClient
): Promise<P2PPaymentInfo> => {
  const result: any = await secretjs.query.compute.queryContract({
    contract_address: params.contract_address,
    code_hash: params.code_hash,
    query: {
      deal_detail: {
        deal_id: params.deal_id,
        key: params.viewing_key,
        address: params.address,
      },
    },
  });
  if (typeof result === "string") {
    throw new Error(result);
  }

  const paymentInfo = {
    ...(result?.deal_detail?.payment_info || {}),
    ...methodToJson(result?.deal_detail?.payment_info?.method || ""),
  };
  console.log({ paymentInfo });
  return paymentInfo;
};

export const setP2PViewingKey = async (
  params: {
    address: string;
    viewing_key: string;
    query_auth: {
      address: string;
      code_hash: string;
    };
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.query_auth.address,
      code_hash: params.query_auth.code_hash,
      msg: {
        set_viewing_key: {
          key: params.viewing_key,
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};

// Set Up Payment Information
export const registerP2PPaymentInfo = async (
  params: {
    contract_address: string;
    code_hash: string;
    address: string;
    method: string;
    detail: string;
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.contract_address,
      code_hash: params.code_hash,
      msg: {
        register_payment_info: {
          method: params.method,
          detail: params.detail,
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};

// Create Post
export const createP2PPost = async (
  params: {
    contract_address: string;
    code_hash: string;
    address: string;
    is_dealer_buy: boolean;
    amount: string;
    min_amount: string;
    settle_currency: string;
    settle_price: string;
    deal_token: {
      address: string;
      code_hash: string;
    };
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.contract_address,
      code_hash: params.code_hash,
      msg: {
        add_post: {
          is_dealer_buy: params.is_dealer_buy,
          amount: params.amount,
          min_amount: params.min_amount,
          settle_currency: params.settle_currency,
          settle_price: params.settle_price,
          deal_token: params.deal_token,
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};

// Cancel Post
export const cancelP2PPost = async (
  params: {
    contract_address: string;
    code_hash: string;
    address: string;
    post_id: string;
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.contract_address,
      code_hash: params.code_hash,
      msg: {
        cancel_post: {
          post_id: params.post_id,
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};

// Create Deal
export const enterP2PDeal = async (
  params: {
    contract_address: string;
    code_hash: string;
    address: string;
    post_id: string;
    amount: string;
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.contract_address,
      code_hash: params.code_hash,
      msg: {
        enter_deal: {
          post_id: params.post_id,
          amount: params.amount,
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};

// Dealer Deposit
export const dealerDeposit = async (
  params: {
    contract_address: string;
    token_address: string;
    token_code_hash: string;
    address: string;
    post_id: string;
    amount: string;
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.token_address,
      code_hash: params.token_code_hash,
      msg: {
        send: {
          recipient: params.contract_address,
          amount: params.amount,
          msg: Buffer.from(
            JSON.stringify({ dealer: { post_id: params.post_id } })
          ).toString("base64"),
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};

// Customer Deposit
export const customerDeposit = async (
  params: {
    contract_address: string;
    token_address: string;
    token_code_hash: string;
    address: string;
    deal_id: string;
    amount: string;
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.token_address,
      code_hash: params.token_code_hash,
      msg: {
        send: {
          recipient: params.contract_address,
          amount: params.amount,
          msg: Buffer.from(
            JSON.stringify({ customer: { deal_id: params.deal_id } })
          ).toString("base64"),
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};

// Confirm Off-Chain Transfer
export const confirmOffChainTransfer = async (
  params: {
    contract_address: string;
    code_hash: string;
    address: string;
    deal_id: string;
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.contract_address,
      code_hash: params.code_hash,
      msg: {
        confirm_bank_transfer: {
          deal_id: params.deal_id,
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};

// Cancel Deal
export const cancelP2PDeal = async (
  params: {
    contract_address: string;
    code_hash: string;
    address: string;
    deal_id: string;
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.contract_address,
      code_hash: params.code_hash,
      msg: {
        cancel_deal: {
          deal_id: params.deal_id,
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};

// Resolve Deal
export const resolveDeal = async (
  params: {
    contract_address: string;
    code_hash: string;
    address: string;
    deal_id: string;
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.contract_address,
      code_hash: params.code_hash,
      msg: {
        resolve_deal: {
          deal_id: params.deal_id,
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};

// Dispute Deal
export const disputeDeal = async (
  params: {
    contract_address: string;
    code_hash: string;
    address: string;
    deal_id: string;
  },
  secretjs: SecretNetworkClient
) => {
  const tx = await secretjs.tx.compute.executeContract(
    {
      sender: params.address,
      contract_address: params.contract_address,
      code_hash: params.code_hash,
      msg: {
        dispute_deal: {
          deal_id: params.deal_id,
        },
      },
    },
    {
      gasLimit: 400000,
      gasPriceInFeeDenom: 0.25,
    }
  );
  console.log(tx);
  if (tx.code !== 0) {
    throw new Error(tx.rawLog);
  }
  return tx;
};
